import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { DrawSVGPlugin } from "gsap/dist/DrawSVGPlugin";
import { GSDevTools } from "gsap/dist/GSDevTools";

gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin, GSDevTools);

/**
 * @file
 * Our purpose page animations.
 */
((Drupal, once) => {
  Drupal.behaviors.tfalOurPurposeAnimation = {
    attach: function attach(context) {
      once(
        "our-purpose-animation-svg",
        "#our-purpose-animation-svg",
        context
      ).forEach(() => {
        gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin, GSDevTools);
        gsap.defaults({ ease: "none" });

        // Setup a wrapper with ID so we don't need to do it manually through layout builder.
        const svgWrapperId = "our-purpose-animation";
        document
          .getElementById("our-purpose-animation-svg")
          .closest(".row")
          .setAttribute("id", svgWrapperId);

        const mm = gsap.matchMedia();
        const breakPoint = 768;

        mm.add(
          {
            isDesktop: `(min-width: ${breakPoint}px)`,
            isMobile: `(max-width: ${breakPoint - 1}px)`,
            reduceMotion: "(prefers-reduced-motion: reduce)",
          },
          (context) => {
            const { isMobile, reduceMotion } = context.conditions;

            //
            // Sections: Title, content and image animations.
            //
            const blocks = gsap.utils.toArray("#our-purpose-animation .block");
            blocks.forEach((block, index) => {
              // Set the alignment for the first block to the top.
              if (index === 0) {
                block.classList.add("justify-content-start");
              }

              // Ping-pong animations.
              if (
                block.classList.contains(
                  "block-inline-blockixm-blocks-ping-pong"
                )
              ) {
                const image = block.querySelector("img");
                const title = block.querySelectorAll(":scope > h2");
                const content = block.querySelector(".ping-pong-content");

                // Block titles: Fade in.
                if (title) {
                  gsap.fromTo(
                    title,
                    {
                      autoAlpha: 0,
                    },
                    {
                      duration: 2,
                      autoAlpha: 1,
                      scrollTrigger: {
                        trigger: title,
                        start: "top-=150px 80%",
                        end: "top 80%",
                        scrub: 1,
                      },
                    },
                    0
                  );
                }

                // Content boxes: Fade in & up.
                if (content) {
                  gsap.fromTo(
                    content,
                    {
                      autoAlpha: 0,
                      y: reduceMotion ? 0 : 50,
                    },
                    {
                      y: 0,
                      autoAlpha: 1,
                      scrollTrigger: {
                        trigger: content,
                        start: isMobile ? "top 80%" : "top-=650px center",
                        end: isMobile ? "top 90%" : "top+=50px 70%",
                        scrub: 1,
                      },
                    },
                    0
                  );
                }

                //
                // Images: Odd/even sets fade & move in from the left/right.
                //
                if (image) {
                  gsap.fromTo(
                    image,
                    {
                      autoAlpha: 0,
                      x: index % 2 === 0 && !reduceMotion ? -50 : 50,
                    },
                    {
                      duration: 2,
                      x: 0,
                      autoAlpha: 1,
                      scrollTrigger: {
                        trigger: image,
                        start: isMobile ? "top 80%" : "top 60%",
                        end: isMobile ? "top 90%" : "top 90%",
                        scrub: 1,
                      },
                    },
                    0
                  );
                }
              } else if (
                !block.classList.contains("block-our-purpose-animation")
              ) {
                // Any other block (except our svg block), fade it up.
                gsap.fromTo(
                  block,
                  {
                    autoAlpha: 0,
                    y: reduceMotion ? 0 : 50,
                  },
                  {
                    duration: 1,
                    y: 0,
                    autoAlpha: 1,
                    scrollTrigger: {
                      trigger: block,
                      start: isMobile ? "top 90%" : "top+=20% 80%",
                      end: isMobile ? "top 85%" : "top+=20% 70%",
                      scrub: 1,
                    },
                  },
                  0
                );
              }
            });
          }
        );

        //
        // Arrow animations.
        //
        const arrows = gsap.utils.toArray(".arrow");
        arrows.forEach((arrow) => {
          gsap.to(
            arrow,
            {
              // This will animate ALL boxes
              duration: 1,
              autoAlpha: 1,
              stagger: 1,
              scrollTrigger: {
                trigger: arrow,
                start: "center-=50% center",
                end: "center-=25% center",
                scrub: 1,
              },
            },
            0
          );
        });

        //
        // Section 1: Dot & first line animation.
        //
        const svgEl = "#our-purpose-animation";
        const lineDraw1 = gsap.timeline({
          scrollTrigger: {
            trigger: svgEl,
            scrub: 1,
            start: "top-=450px 80%",
            end: "top-=20px 60%",
          },
        });

        lineDraw1
          .fromTo(
            "#theDot",
            1,
            {
              autoAlpha: 0,
              drawSVG: "0% 0%",
            },
            {
              autoAlpha: 1,
              drawSVG: "0% 220",
            },
            ">=0.25"
          )
          .fromTo(
            "#theLine",
            1,
            {
              autoAlpha: 0,
              drawSVG: "0% 0%",
            },
            {
              autoAlpha: 1,
              drawSVG: "0% 220",
            },
            ">=0.25"
          );

        //
        // Section 2: Second line segment.
        //
        const lineDraw2 = gsap.timeline({
          scrollTrigger: {
            trigger: svgEl,
            scrub: 1,
            start: "top=0 80%",
            end: "top+=350px center",
          },
        });
        lineDraw2.to(
          "#theLine",
          {
            startAt: {
              drawSVG: "0 220",
            },
            drawSVG: "0% 1350",
          },
          ">=2"
        );

        //
        // Section 3: Third line segment.
        //
        const lineDraw3 = gsap.timeline({
          scrollTrigger: {
            trigger: svgEl,
            scrub: 1,
            start: "top+=550px center",
            end: "top+=900px 30%",
          },
        });
        lineDraw3.to(
          "#theLine",
          {
            startAt: {
              drawSVG: "0 1350",
            },
            drawSVG: "0% 2600",
          },
          ">=1"
        );

        //
        // Section 4: Fourth line segment.
        //
        const lineDraw4 = gsap.timeline({
          id: "lineDraw 4",
          scrollTrigger: {
            trigger: svgEl,
            scrub: 1,
            start: "top+=1000px center",
            end: "top+=1700px 30%",
          },
        });
        lineDraw4.to(
          "#theLine",
          {
            startAt: {
              drawSVG: "0 2600",
            },
            drawSVG: "0% 3800",
          },
          ">=1"
        );

        //
        // Section 5: Fifth line segment.
        //
        const lineDraw5 = gsap.timeline({
          scrollTrigger: {
            trigger: svgEl,
            scrub: 1,
            start: "top+=2000px center",
            end: "top+=2400 center",
          },
        });
        lineDraw5.to(
          "#theLine",
          {
            startAt: {
              drawSVG: "0 3800",
            },
            drawSVG: "0% 100%",
          },
          ">=1"
        );
      });
    },
  };
})(Drupal, once);
